/**
 * Service for loading static JSON data files served by Cloudflare Pages CDN.
 * 5-minute in-session cache prevents redundant fetches across components.
 */

const dataCache = new Map()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

async function loadJSON(filename) {
  const cached = dataCache.get(filename)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data
  }

  try {
    const response = await fetch(`/data/${filename}`)
    if (!response.ok) {
      throw new Error(`Failed to load ${filename}: ${response.status}`)
    }
    const data = await response.json()
    dataCache.set(filename, { data, timestamp: Date.now() })
    return data
  } catch (error) {
    console.error(`Error loading ${filename}:`, error)
    if (cached) {
      console.log(`Returning stale cache for ${filename}`)
      return cached.data
    }
    return null
  }
}

export async function getDriversFull() {
  return loadJSON('drivers_full.json')
}

export async function getDriversMetadata() {
  return loadJSON('drivers_metadata.json')
}

export async function getPeakRankings() {
  return loadJSON('peak_rankings.json')
}

export async function getCurrentGrid() {
  return loadJSON('current_grid.json')
}

export async function getTimelineData() {
  return loadJSON('timeline_data.json')
}

export async function getTimelineYear(year) {
  const timeline = await getTimelineData()
  return timeline?.[year] || []
}

export async function getEraMarkers() {
  return loadJSON('era_markers.json')
}

export async function getTeamColors() {
  return loadJSON('team_colors.json')
}

export async function getApiStats() {
  return loadJSON('api_stats.json')
}

export async function getDriverById(driverId) {
  const drivers = await getDriversFull()
  return drivers?.find(d => d.id === driverId) || null
}

export async function getDriverHistory(driverId) {
  const driver = await getDriverById(driverId)
  return driver?.history || []
}

export async function searchDrivers(query, limit = 20) {
  if (!query || query.length < 2) return []
  const drivers = await getDriversFull()
  if (!drivers) return []
  const queryLower = query.toLowerCase()
  return drivers
    .filter(d =>
      d.name?.toLowerCase().includes(queryLower) ||
      d.id?.toLowerCase().includes(queryLower)
    )
    .slice(0, limit)
}

export function clearCache() {
  dataCache.clear()
}

export default {
  getDriversFull,
  getDriversMetadata,
  getPeakRankings,
  getCurrentGrid,
  getTimelineData,
  getTimelineYear,
  getEraMarkers,
  getTeamColors,
  getApiStats,
  getDriverById,
  getDriverHistory,
  searchDrivers,
  clearCache
}
